import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Input } from "./Controls/Input";
import { PhoneInput } from "./Controls/PhoneInput";
import { Select } from "./Controls/Select";
import { Button } from "./Controls/Button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StaffRole } from "./Data/ApiTransport";
import { app_roles, RoleList, isAdminRole, requiresFacilityAssignment, roleHasGranularPermissions } from "./helpers/RoleManager";
import { FacilityApi, ServerTransferMonitorMonitorPermissionDto, ServerTransferMonitorStaff, StaffApi } from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";
import Multiselect from 'react-select';
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { MONITOR_PERMISSIONS } from "./helpers/AuthorizationManager";
import { Search } from "react-bootstrap-icons";
import "./OperatorStaffModal.scss";

interface OperatorStaffModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    onSave: (data: any) => void | Promise<ServerTransferMonitorStaff | void>;
    onEditPermissionsAfterInvite?: (staff: ServerTransferMonitorStaff) => void;
    initialData?: ServerTransferMonitorStaff;
    mode: 'invite' | 'edit';
}

export interface StaffFormData {
    selectedRole: string;
    name?: string | null;
    emailAddress?: string | null;
    phoneNumber?: string | null;
    note?: string | null;
    staffId?: number;
    operatorId?: number;
    role?: any;
    roles?: string[];
    facilities?: any[];
    password?: string;
    createdWhen?: any;
    removedWhen?: any;
}

const facilityUserDefaults = MONITOR_PERMISSIONS.map(p => p.key);
const staffApi = createApiService(StaffApi);

type PermissionCatalogItem = ServerTransferMonitorMonitorPermissionDto;

function initialsFromName(name?: string | null) {
    const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2);
    return `${parts[0][0]}${parts[parts.length - 1][0]}`;
}

export default function OperatorStaffModal({ show, setShow, onSave, onEditPermissionsAfterInvite, initialData, mode }: OperatorStaffModalProps) {
    const roles = app_roles.filter(c => c.identity != RoleList.SuperAdmin.identity);
    const { register, reset, watch, formState: { errors }, handleSubmit } = useForm<StaffFormData>();
    const selectedRoleField = register("selectedRole", { required: "Role is required" });
    const [auth] = useAtom(authAtom);
    const canEditPermissions = isAdminRole(auth.account?.roles ?? []);

    const [facilities, setFacilities] = useState<any>([]);
    const [showFacilities, setShowFacilities] = useState(false);
    const [selectedValues, setSelectedValues] = useState<any>([]);
    const [catalog, setCatalog] = useState<PermissionCatalogItem[]>([]);
    const [enabledPermissions, setEnabledPermissions] = useState<string[]>([]);
    const [permissionSearch, setPermissionSearch] = useState("");
    const [editPermissionsAfterSave, setEditPermissionsAfterSave] = useState(false);
    const [permListScroll, setPermListScroll] = useState({ canScroll: false, atEnd: true });
    const permListRef = useRef<HTMLDivElement>(null);
    const facilitiesApi = createApiService(FacilityApi);

    const updatePermListScroll = useCallback(() => {
        const el = permListRef.current;
        if (!el) return;
        const canScroll = el.scrollHeight > el.clientHeight + 1;
        const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
        setPermListScroll({ canScroll, atEnd });
    }, []);

    const selectedRole = watch('selectedRole');
    const watchedName = watch('name');
    const watchedEmail = watch('emailAddress');
    const showPermissionsPanel = mode === 'edit' && canEditPermissions && roleHasGranularPermissions(selectedRole);
    const showInvitePermissionsToggle = mode === 'invite' && canEditPermissions && roleHasGranularPermissions(selectedRole);

    const applyRolePermissionDefaults = useCallback((role?: string | null) => {
        if (!roleHasGranularPermissions(role)) {
            setEnabledPermissions([]);
            return;
        }
        if (role === RoleList.FacilityUser.name) {
            setEnabledPermissions([...facilityUserDefaults]);
        }
    }, []);

    const groupedPermissions = useMemo(() => {
        const items = catalog.length > 0 ? catalog : MONITOR_PERMISSIONS.map(p => ({
            key: p.key,
            displayName: p.displayName,
            description: p.description,
            category: p.category,
            sortOrder: p.sortOrder,
        }));
        return items.reduce<Record<string, PermissionCatalogItem[]>>((acc, item) => {
            const cat = item.category || 'Other';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
        }, {});
    }, [catalog]);

    const allPermissionKeys = useMemo(() => {
        const items = catalog.length > 0 ? catalog : MONITOR_PERMISSIONS.map(p => ({
            key: p.key,
            displayName: p.displayName,
            description: p.description,
            category: p.category,
            sortOrder: p.sortOrder,
        }));
        return items.map(item => item.key).filter((key): key is string => !!key);
    }, [catalog]);

    const enabledPermissionCount = useMemo(
        () => enabledPermissions.filter(key => allPermissionKeys.includes(key)).length,
        [allPermissionKeys, enabledPermissions]
    );

    const filteredCategoryEntries = useMemo(() => {
        const q = permissionSearch.trim().toLowerCase();
        const entries = Object.entries(groupedPermissions)
            .map(([category, items]) => {
                const sorted = [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                const filtered = sorted.filter(item =>
                    !q ||
                    (item.displayName ?? "").toLowerCase().includes(q) ||
                    (item.description ?? "").toLowerCase().includes(q) ||
                    (item.category ?? "").toLowerCase().includes(q) ||
                    (item.key ?? "").toLowerCase().includes(q)
                );
                return [category, filtered] as [string, PermissionCatalogItem[]];
            })
            .filter(([, items]) => items.length > 0)
            .sort(([, itemsA], [, itemsB]) => {
                const minA = Math.min(...itemsA.map(i => i.sortOrder ?? 0));
                const minB = Math.min(...itemsB.map(i => i.sortOrder ?? 0));
                return minA - minB;
            });
        return entries;
    }, [groupedPermissions, permissionSearch]);

    const resetData = async (data?: ServerTransferMonitorStaff) => {
        if (mode === 'edit' && data) {
            const firstRole = data?.roles?.[0] ?? null;
            const staffEdit = {
                createdWhen: data?.createdWhen,
                emailAddress: data?.emailAddress,
                facilities: data?.facilities,
                name: data?.name,
                note: data?.note,
                operatorId: data?.operatorId,
                phoneNumber: data?.phoneNumber,
                removedWhen: data?.removedWhen,
                role: data?.role,
                roles: data?.roles,
                selectedRole: firstRole || "",
                staffId: data?.staffId
            };
            reset(staffEdit as any);
            const facilityOptions = data?.facilities?.map(c => ({
                label: c.name,
                value: c.facilityId
            })) || [];
            setSelectedValues(facilityOptions);
            setShowFacilities(requiresFacilityAssignment(firstRole?.toUpperCase()));

            if (canEditPermissions && data.staffId && roleHasGranularPermissions(firstRole)) {
                const staff = await staffApi.getStaff({ id: data.staffId }).then(res => res.data.item);
                setEnabledPermissions(staff?.permissions ?? []);
            } else {
                setEnabledPermissions([]);
            }
        } else {
            reset({
                name: "",
                emailAddress: "",
                phoneNumber: "",
                password: "",
                note: "",
                role: StaffRole.OperatorAdmin,
                selectedRole: roles[0]?.text || ""
            });
            setSelectedValues([]);
            setShowFacilities(requiresFacilityAssignment(roles[0]?.identity));
            setEnabledPermissions([]);
            setEditPermissionsAfterSave(false);
        }
    };

    useEffect(() => {
        if (show) {
            facilitiesApi.listFacilitiesLite().then(result => {
                const opts = (result.data.items || []).map(c => ({
                    label: c.name,
                    value: c.facilityId
                }));
                setFacilities(opts);
            });
            if (canEditPermissions) {
                staffApi.listStaffPermissions()
                    .then(res => setCatalog(res.data.items ?? []))
                    .catch(() => setCatalog([]));
            }
            resetData(initialData);
        } else {
            setPermissionSearch("");
            setEditPermissionsAfterSave(false);
        }
    }, [show, initialData, mode]);

    useEffect(() => {
        if (!show || !showPermissionsPanel) return;
        updatePermListScroll();
        const el = permListRef.current;
        if (!el) return;
        el.addEventListener("scroll", updatePermListScroll, { passive: true });
        const observer = new ResizeObserver(updatePermListScroll);
        observer.observe(el);
        return () => {
            el.removeEventListener("scroll", updatePermListScroll);
            observer.disconnect();
        };
    }, [show, showPermissionsPanel, filteredCategoryEntries, updatePermListScroll]);

    function selectedFacilityChange(selectedOptions: any) {
        setSelectedValues(selectedOptions || []);
    }

    function selectedRoleChange(roleName: string) {
        setShowFacilities(requiresFacilityAssignment(roleName.toUpperCase()));
        if (!roleHasGranularPermissions(roleName)) {
            setEditPermissionsAfterSave(false);
        }
        if (mode === 'edit') {
            applyRolePermissionDefaults(roleName);
        }
    }

    function togglePermission(key: string, enabled: boolean) {
        setEnabledPermissions(prev =>
            enabled ? [...prev, key] : prev.filter(k => k !== key)
        );
    }

    const selectAll = () => {
        setSelectedValues(facilities);
    };

    const selectAllPermissions = () => {
        setEnabledPermissions([...allPermissionKeys]);
    };

    const selectNoPermissions = () => {
        setEnabledPermissions([]);
    };

    async function submit(data: StaffFormData) {
        const facilitiesForApi = selectedValues.map((option: any) => ({ facilityId: option.value }));

        const submitData: any = {
            ...data,
            roles: [data.selectedRole],
            facilities: facilitiesForApi,
        };

        if (showPermissionsPanel) {
            submitData.permissions = enabledPermissions;
        }

        const shouldEditPermissionsAfterSave =
            mode === 'invite'
            && editPermissionsAfterSave
            && roleHasGranularPermissions(data.selectedRole);

        try {
            const savedStaff = await Promise.resolve(onSave(submitData));
            setShow(false);

            if (shouldEditPermissionsAfterSave && savedStaff) {
                onEditPermissionsAfterInvite?.(savedStaff);
            }
        } catch {
            // Parent handles error feedback.
        }
    }

    const modalTitle = mode === 'invite' ? 'Invite staff member' : 'Edit staff member';
    const headerEmail = watchedEmail || initialData?.emailAddress || "";
    const headerInitials = initialsFromName(watchedName || initialData?.name);

    return (
        <Modal
            show={show}
            centered
            dialogClassName="operator-staff-modal"
            onHide={() => setShow(false)}
        >
            <Form onSubmit={handleSubmit(submit)}>
                <Modal.Header closeButton>
                    <div className="staff-modal-header">
                        <div className="staff-modal-avatar" aria-hidden="true">
                            {headerInitials}
                        </div>
                        <div className="min-w-0">
                            <h2 className="staff-modal-title">{modalTitle}</h2>
                            {headerEmail && (
                                <div className="staff-modal-subtitle">{headerEmail}</div>
                            )}
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="staff-modal-columns">
                        <div className="staff-modal-profile">
                            <div className="staff-modal-section-label">Profile</div>

                            <Input
                                type="text"
                                label={mode === 'invite' ? 'Name' : 'Name'}
                                {...register("name", { required: "Name is required" })}
                                placeholder="Staff member name"
                                error={errors.name}
                            />

                            <Input
                                type="email"
                                label={mode === 'invite' ? 'Email address' : 'Email address'}
                                {...register("emailAddress", {
                                    required: "Email is required",
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: "Invalid email address"
                                    }
                                })}
                                placeholder="staff@example.com"
                                error={errors.emailAddress}
                            />

                            <PhoneInput
                                label="Phone number (optional)"
                                explanation="Optional"
                                placeholder="555-555-5555"
                                error={errors.phoneNumber}
                                {...register("phoneNumber", {
                                    validate: (value) =>
                                        !value || /^\d{3}-\d{3}-\d{4}$/.test(value)
                                        || "Phone number must be in format ###-###-####",
                                })}
                            />

                            <Select
                                label="Role"
                                {...selectedRoleField}
                                onChange={(e) => {
                                    void selectedRoleField.onChange(e);
                                    selectedRoleChange(e.target.value);
                                }}
                                error={errors.selectedRole}
                            >
                                {roles.map(role => (
                                    <option key={role.identity} value={role.text}>
                                        {role.text}
                                    </option>
                                ))}
                            </Select>

                            {selectedRole === RoleList.TransientReviewer.name && (
                                <p className="text-muted small mb-3">
                                    This role has limited access — Transient sessions only.
                                </p>
                            )}

                            {showInvitePermissionsToggle && (
                                <div className="staff-invite-perm-toggle mb-3">
                                    <div className="staff-invite-perm-toggle-row">
                                        <div className="staff-invite-perm-toggle-copy">
                                            <div className="staff-invite-perm-toggle-title">Edit permissions after invite</div>
                                            <div className="staff-invite-perm-toggle-desc">
                                                Role defaults apply on invite. Open the permissions editor after the account is created.
                                            </div>
                                        </div>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="edit-permissions-after-save"
                                                checked={editPermissionsAfterSave}
                                                onChange={(e) => setEditPermissionsAfterSave(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(facilities && showFacilities) && (
                                <div className="mb-3">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <Form.Label className="mb-0">Facilities</Form.Label>
                                        {facilities.length > 1 && (
                                            <button
                                                type="button"
                                                className="staff-modal-select-all"
                                                onClick={selectAll}
                                            >
                                                Select all
                                            </button>
                                        )}
                                    </div>
                                    <Multiselect
                                        onChange={selectedFacilityChange}
                                        value={selectedValues}
                                        isMulti
                                        options={facilities}
                                    />
                                </div>
                            )}

                            <Input
                                as="textarea"
                                type="text"
                                label={mode === 'invite' ? 'Note' : 'Notes'}
                                {...register("note")}
                                placeholder="Staff member notes"
                                rows={3}
                                error={errors.note}
                            />
                        </div>

                        {showPermissionsPanel && (
                            <div className="staff-modal-permissions">
                                <div className="staff-perm-header">
                                    <div className="staff-perm-header-left">
                                        <span className="staff-modal-section-label staff-perm-header-label">Permissions</span>
                                        <span className="staff-perm-count-badge">
                                            {enabledPermissionCount} of {allPermissionKeys.length}
                                        </span>
                                    </div>
                                    <div className="staff-perm-bulk-actions">
                                            <button
                                                type="button"
                                                className="staff-perm-bulk-link"
                                                onClick={selectAllPermissions}
                                            >
                                                All
                                            </button>
                                            <span className="staff-perm-bulk-sep" aria-hidden="true">·</span>
                                            <button
                                                type="button"
                                                className="staff-perm-bulk-link staff-perm-bulk-link--muted"
                                                onClick={selectNoPermissions}
                                            >
                                                None
                                            </button>
                                        </div>
                                </div>

                                {enabledPermissionCount === 0 && (
                                    <p className="staff-perm-invite-note">
                                        No permissions selected — staff can browse assigned facilities but cannot perform restricted actions. Staff must sign in again for changes to apply.
                                    </p>
                                )}

                                <div className="staff-perm-search">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <Search size={14} className="text-muted" />
                                        </span>
                                        <input
                                            type="search"
                                            className="form-control"
                                            placeholder="Search permissions..."
                                            value={permissionSearch}
                                            onChange={(e) => setPermissionSearch(e.target.value)}
                                            aria-label="Search permissions"
                                        />
                                    </div>
                                </div>

                                <div
                                    className={[
                                        "staff-perm-list-wrap",
                                        !permListScroll.canScroll && "staff-perm-list-wrap--no-scroll",
                                        permListScroll.atEnd && "staff-perm-list-wrap--at-end",
                                    ].filter(Boolean).join(" ")}
                                >
                                <div
                                    ref={permListRef}
                                    className="staff-perm-list"
                                >
                                    {filteredCategoryEntries.length === 0 ? (
                                        <div className="staff-perm-empty">
                                            No permissions match your search.
                                        </div>
                                    ) : (
                                        filteredCategoryEntries.map(([category, items]) => (
                                            <div key={category} className="staff-perm-category">
                                                <div className="staff-perm-category-label">{category}</div>
                                                {items.filter(item => item.key).map(item => {
                                                    const key = item.key!;
                                                    return (
                                                    <div key={key} className="staff-perm-item">
                                                        <div className="staff-perm-item-row">
                                                            <div className="staff-perm-copy">
                                                                <div className="staff-perm-title">{item.displayName}</div>
                                                                {item.description && (
                                                                    <div className="staff-perm-desc">{item.description}</div>
                                                                )}
                                                            </div>
                                                            <div className="staff-perm-switch form-check form-switch mb-0">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    id={`perm-${key}`}
                                                                    checked={enabledPermissions.includes(key)}
                                                                    onChange={(e) => togglePermission(key, e.target.checked)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        ))
                                    )}
                                </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="d-flex">
                    <Button className="flex-grow-1" onClick={() => setShow(false)}>Cancel</Button>
                    <Button className="flex-grow-1 btn-main" type="submit">
                        {mode === 'invite' ? 'Invite' : 'Save changes'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
